import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/core/lib/utils';

export const floatingInputVariants = cva(
  [
    'peer w-full h-9 rounded-lg bg-background',
    'outline outline-1 outline-input',
    'text-foreground text-sm placeholder-transparent',
    'transition-all duration-150',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'focus:outline-primary focus:ring-2 focus:ring-primary/20',
        outline: [
          'bg-transparent border border-input',
          'focus:border-primary focus:ring-2 focus:ring-primary/20',
        ].join(' '),
        filled: [
          'bg-muted',
          'focus:bg-background focus:outline-primary focus:ring-2 focus:ring-primary/20',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const floatingLabelVariants = cva(
  [
    'absolute left-3 px-1',
    'pointer-events-none select-none',
    'transition-all duration-150',
    'bg-background',
  ].join(' '),
  {
    variants: {
      variant: {
        default: '',
        outline:
          'bg-transparent peer-focus:bg-background peer-[&:not(:placeholder-shown)]:bg-background',
        filled: 'bg-muted peer-focus:bg-background peer-[&:not(:placeholder-shown)]:bg-background',
      },
      floating: {
        true: 'top-0 -translate-y-1/2 text-xs text-foreground',
        false: 'top-1/2 -translate-y-1/2 text-sm text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
      floating: false,
    },
  },
);

interface FloatingLabelInputProps
  extends
    Omit<React.ComponentProps<'input'>, 'placeholder'>,
    VariantProps<typeof floatingInputVariants> {
  label: string;
  id: string;
  hasValue?: boolean;
  invalid?: boolean;
  endElement?: React.ReactNode;
}

export function FloatingLabelInput({
  className,
  type,
  variant,
  label,
  id,
  hasValue: hasValueProp = false,
  invalid = false,
  disabled,
  endElement,
  ...props
}: FloatingLabelInputProps) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [prevHasValueProp, setPrevHasValueProp] = React.useState(hasValueProp);
  const [hasInternalValue, setHasInternalValue] = React.useState(hasValueProp);

  // Sync external reset (e.g. form reset) without useEffect — React Compiler-compatible
  if (prevHasValueProp !== hasValueProp) {
    setPrevHasValueProp(hasValueProp);
    setHasInternalValue(hasValueProp);
  }

  const isFloating = isFocused || hasInternalValue;

  // Padding derecho dinámico cuando hay endElement
  const inputPaddingX = endElement ? 'px-3 pr-10' : 'px-3';

  return (
    <div className="relative w-full flex items-center">
      <input
        {...props}
        id={id}
        type={type}
        placeholder=""
        data-slot="input"
        disabled={disabled}
        aria-invalid={invalid}
        onFocus={(e) => {
          setIsFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          // Evalúa el valor real del DOM al perder el foco
          setHasInternalValue(e.target.value !== '');
          props.onBlur?.(e);
        }}
        onChange={(e) => {
          // Actualiza en tiempo real mientras escribe
          setHasInternalValue(e.target.value !== '');
          props.onChange?.(e);
        }}
        className={cn(
          floatingInputVariants({ variant }),
          inputPaddingX,
          invalid && 'outline-destructive ring-2 ring-destructive/20 focus:outline-destructive',
          className,
        )}
      />

      {/* Label flotante */}
      <label htmlFor={id} className={cn(floatingLabelVariants({ variant, floating: isFloating }))}>
        {label}
      </label>

      {/* Elemento derecho — toggle de visibilidad, etc. */}
      {endElement && (
        <div className="absolute right-3 flex items-center" onMouseDown={(e) => e.preventDefault()}>
          {endElement}
        </div>
      )}
    </div>
  );
}
